import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Message, Icon } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { isAdmin } from '../../helpers/utils';
import { renderQuotaWithPrompt } from '../../helpers/render';
import { StudioFormHeader, StudioPage } from '../../components/StudioPage';

const sectionStyle = {
  padding: '16px 18px',
  borderRadius: 12,
  background: 'rgba(255,255,255,0.5)',
  border: '1px solid rgba(24,24,27,0.06)',
  marginBottom: 14,
};

const sectionLabelStyle = {
  fontSize: 10,
  fontWeight: 800,
  color: '#4f63a8',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const fieldStyle = {
  marginBottom: 10,
};

const btnStyle = {
  fontSize: 12,
  padding: '6px 14px',
  borderRadius: 8,
  border: '1px solid rgba(24,24,27,0.1)',
  background: 'rgba(255,255,255,0.6)',
  color: '#52525b',
  cursor: 'pointer',
  transition: 'all 0.2s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [modelOptions, setModelOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [channelNameOptions, setChannelNameOptions] = useState([]);
  const originInputs = {
    name: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    models: [],
    subnet: '',
    channel_ids: [],
    channel_names: [],
    load_balance_strategy: '',
    pool_access: 'channel',
  };
  const loadBalanceStrategyOptions = [
    { key: '', value: '', text: t('token.edit.load_balance_strategy_inherit') },
    { key: 'random', value: 'random', text: t('token.edit.lb.random') },
    { key: 'weighted-random', value: 'weighted-random', text: t('token.edit.lb.weighted_random') },
    { key: 'round-robin', value: 'round-robin', text: t('token.edit.lb.round_robin') },
    { key: 'least-connections', value: 'least-connections', text: t('token.edit.lb.least_connections') },
    { key: 'ip-hash', value: 'ip-hash', text: t('token.edit.lb.ip_hash') },
  ];
  const [inputs, setInputs] = useState(originInputs);
  const { name, remain_quota, expired_time, unlimited_quota } = inputs;
  const navigate = useNavigate();
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const handleCancel = () => { navigate('/token'); };
  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60 + day * 24 * 60 * 60 + hour * 60 * 60 + minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };
  const setUnlimitedQuota = () => { setInputs({ ...inputs, unlimited_quota: !unlimited_quota }); };

  const loadToken = async () => {
    try {
      let res = await API.get(`/api/token/${tokenId}`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        if (data.expired_time !== -1) data.expired_time = timestamp2string(data.expired_time);
        data.models = data.models === '' ? [] : data.models.split(',');
        data.channel_ids = data.channel_ids ? data.channel_ids.split(',').filter(Boolean).map(Number) : [];
        data.channel_names = data.channel_names ? data.channel_names.split(',').filter(Boolean) : [];
        if (!data.pool_access) data.pool_access = 'channel';
        setInputs(data);
      } else { showError(message || 'Failed to load token'); }
    } catch (error) { showError(error.message || 'Network error'); }
    setLoading(false);
  };

  const loadAvailableModels = async () => {
    try {
      let res = await API.get(`/api/user/available_models`);
      const { success, data } = res.data || {};
      if (success && data) setModelOptions(data.map((m) => ({ key: m, text: m, value: m })));
    } catch (error) { /* silent */ }
  };

  const loadChannels = async () => {
    try {
      let res = await API.get(`/api/channel/?size=0`);
      const { success, data } = res.data || {};
      if (success && data) {
        setChannelOptions(data.map((ch) => ({ key: ch.id, text: `#${ch.id} ${ch.name}`, value: ch.id })));
        const nameSet = new Set();
        const names = [];
        data.forEach((ch) => { if (ch.name && !nameSet.has(ch.name)) { nameSet.add(ch.name); names.push({ key: ch.name, text: ch.name, value: ch.name }); } });
        setChannelNameOptions(names);
      }
    } catch (error) { /* silent */ }
  };

  useEffect(() => {
    if (isEdit) loadToken().catch(() => setLoading(false));
    loadAvailableModels().catch(() => {});
    if (isAdmin()) loadChannels();
  }, []);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    let localInputs = { ...inputs };
    localInputs.remain_quota = parseInt(localInputs.remain_quota);
    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) { showError(t('token.edit.messages.expire_time_invalid')); return; }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    localInputs.models = localInputs.models.join(',');
    localInputs.channel_ids = localInputs.channel_ids.join(',');
    localInputs.channel_names = localInputs.channel_names.join(',');
    let res;
    try {
      if (isEdit) { res = await API.put(`/api/token/`, { ...localInputs, id: parseInt(tokenId) }); }
      else { res = await API.post(`/api/token/`, localInputs); }
      const { success, message } = res.data;
      if (success) {
        if (isEdit) showSuccess(t('token.edit.messages.update_success'));
        else { showSuccess(t('token.edit.messages.create_success')); setInputs(originInputs); }
      } else { showError(message); }
    } catch (error) {
      // 修复：网络错误/非 200 响应原先抛 TypeError 未捕获，用户无提示
      showError(error.response?.data?.message || error.message);
    }
  };

  const poolAccessOptions = [
    { key: 'channel', value: 'channel', text: t('token.edit.pool_access_channel'), content: (<div><Icon name='sitemap' /> <strong>{t('token.edit.pool_access_channel')}</strong><br/><span style={{fontSize:11,color:'#a1a1aa'}}>{t('token.edit.pool_access_channel_desc')}</span></div>) },
    { key: 'pool', value: 'pool', text: t('token.edit.pool_access_pool'), content: (<div><Icon name='server' /> <strong>{t('token.edit.pool_access_pool')}</strong><br/><span style={{fontSize:11,color:'#a1a1aa'}}>{t('token.edit.pool_access_pool_desc')}</span></div>) },
    { key: 'both', value: 'both', text: t('token.edit.pool_access_both'), content: (<div><Icon name='random' /> <strong>{t('token.edit.pool_access_both')}</strong><br/><span style={{fontSize:11,color:'#a1a1aa'}}>{t('token.edit.pool_access_both_desc')}</span></div>) },
  ];

  const expireButtons = [
    { label: t('token.edit.buttons.never_expire'), m: 0, d: 0, h: 0, min: 0, icon: 'infinity' },
    { label: t('token.edit.buttons.expire_1_month'), m: 1, d: 0, h: 0, min: 0, icon: 'calendar' },
    { label: t('token.edit.buttons.expire_1_day'), m: 0, d: 1, h: 0, min: 0, icon: 'clock' },
    { label: t('token.edit.buttons.expire_1_hour'), m: 0, d: 0, h: 1, min: 0, icon: 'hourglass' },
    { label: t('token.edit.buttons.expire_1_minute'), m: 0, d: 0, h: 0, min: 1, icon: 'wait' },
  ];

  return (
    <StudioPage className='studio-token-form-page'>
      <div className='studio-surface studio-form-shell'>
        <div style={{ padding: 24 }}>
          <StudioFormHeader icon={isEdit ? 'key' : 'plus'} title={isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')} />
          <Form loading={loading} autoComplete='new-password'>

            {/* ─── 基本信息 ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}><Icon name='info circle' size='small' /> {t('token.edit.section_basic')}</div>
              <Form.Field style={fieldStyle}>
                <Form.Input label={t('token.edit.name')} name='name' placeholder={t('token.edit.name_placeholder')}
                  onChange={handleInputChange} value={name} autoComplete='new-password' required={!isEdit} />
              </Form.Field>
              <Form.Field style={fieldStyle}>
                <Form.Dropdown label={t('token.edit.models')} placeholder={t('token.edit.models_placeholder')} name='models'
                  fluid multiple search selection
                  onLabelClick={(e, { value }) => copy(value).then()}
                  onChange={handleInputChange} value={inputs.models} autoComplete='new-password' options={modelOptions} />
              </Form.Field>
            </div>

            {/* ─── 路由配置 ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}><Icon name='route' size='small' /> {t('token.edit.section_route')}</div>
              <Form.Field style={fieldStyle}>
                <Form.Dropdown label={t('token.edit.pool_access') || '路由类型'} name='pool_access'
                  fluid selection onChange={handleInputChange} value={inputs.pool_access || 'channel'}
                  options={poolAccessOptions} />
              </Form.Field>
              {isAdmin() && (
                <Form.Field style={fieldStyle}>
                  <Form.Dropdown label={t('token.edit.load_balance_strategy')} placeholder={t('token.edit.load_balance_strategy_placeholder')}
                    name='load_balance_strategy' fluid selection clearable search
                    onChange={handleInputChange} value={inputs.load_balance_strategy} options={loadBalanceStrategyOptions} />
                </Form.Field>
              )}
              {isAdmin() && (
                <Form.Field style={fieldStyle}>
                  <Form.Dropdown label={t('token.edit.channel')} placeholder={t('token.edit.channel_placeholder')}
                    name='channel_ids' fluid multiple clearable search selection
                    onChange={handleInputChange} value={inputs.channel_ids} options={channelOptions} />
                </Form.Field>
              )}
              {isAdmin() && (
                <Form.Field style={fieldStyle}>
                  <Form.Dropdown label={t('token.edit.channel_name')} placeholder={t('token.edit.channel_name_placeholder')}
                    name='channel_names' fluid multiple clearable search selection
                    onChange={handleInputChange} value={inputs.channel_names} options={channelNameOptions} />
                </Form.Field>
              )}
            </div>

            {/* ─── 访问控制 ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}><Icon name='shield' size='small' /> {t('token.edit.section_access')}</div>
              <Form.Field style={fieldStyle}>
                <Form.Input label={t('token.edit.ip_limit')} name='subnet' placeholder={t('token.edit.ip_limit_placeholder')}
                  onChange={handleInputChange} value={inputs.subnet} autoComplete='new-password' />
              </Form.Field>
              <Form.Field style={fieldStyle}>
                <Form.Input label={t('token.edit.expire_time')} name='expired_time' placeholder={t('token.edit.expire_time_placeholder')}
                  onChange={handleInputChange} value={expired_time} autoComplete='new-password' type='datetime-local' />
              </Form.Field>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {expireButtons.map((btn) => (
                  <button key={btn.label} type='button' style={btnStyle}
                    onClick={() => setExpiredTime(btn.m, btn.d, btn.h, btn.min)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,99,168,0.08)'; e.currentTarget.style.borderColor = 'rgba(79,99,168,0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(24,24,27,0.1)'; }}>
                    <Icon name={btn.icon} size='small' style={{ margin: 0 }} /> {btn.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ─── 额度 ─── */}
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}><Icon name='gem' size='small' /> {t('token.edit.section_quota')}</div>
              <Message size='tiny' info style={{ borderRadius: 8, marginBottom: 10, fontSize: 12 }}>
                <Icon name='info circle' /> {t('token.edit.quota_notice')}
              </Message>
              <Form.Field style={fieldStyle}>
                <Form.Input label={`${t('token.edit.quota')}${renderQuotaWithPrompt(remain_quota, t)}`}
                  name='remain_quota' placeholder={t('token.edit.quota_placeholder')}
                  onChange={handleInputChange} value={remain_quota} autoComplete='new-password'
                  type='number' disabled={unlimited_quota} fluid />
              </Form.Field>
              <button type='button' style={{ ...btnStyle, marginBottom: 0 }}
                onClick={setUnlimitedQuota}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,99,168,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; }}>
                <Icon name={unlimited_quota ? 'lock' : 'lock open'} size='small' style={{ margin: 0 }} />
                {unlimited_quota ? t('token.edit.buttons.cancel_unlimited') : t('token.edit.buttons.unlimited_quota')}
              </button>
            </div>

            {/* ─── 操作按钮 ─── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, marginTop: 4, paddingTop: 16,
              borderTop: '1px solid rgba(24,24,27,0.08)',
            }}>
              <span style={{ fontSize: 11, color: '#a1a1aa' }}>
                <Icon name='info circle' size='small' />
                {isEdit ? t('token.edit.hint_edit_effective') : t('token.edit.hint_create_random_key')}
              </span>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button type='button' onClick={handleCancel} size='small' basic
                  style={{ borderRadius: 10 }}>{t('token.edit.buttons.cancel')}</Button>
                <Button type='button' positive onClick={submit} size='small'
                  style={{ borderRadius: 10 }}
                  icon={isEdit ? 'check' : 'plus'}
                  content={t('token.edit.buttons.submit')} />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </StudioPage>
  );
};

export default EditToken;
